/**
 * classRecorder.js — Sep 2026
 * Teacher-browser class recorder (Option B):
 * composites whiteboard canvas + screen-share video into an offscreen
 * canvas (~1280×720 @ 12fps) and mixes all room audio via WebAudio.
 * Chunks are written to OPFS during class — ZERO network usage while live.
 */

const IS_MOBILE = navigator.userAgentData?.mobile === true
  || /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
// Thermal budget (Sep 2026): phones melt on 720p software encode — drop
// resolution/fps/bitrate on mobile, and prefer VP8 everywhere (≈3× lighter
// software ENCODE for the teacher and far wider hardware DECODE coverage
// on students' phones than VP9).
const FPS = IS_MOBILE ? 8 : 10;
const REC_W = IS_MOBILE ? 854 : 1280;
const REC_H = IS_MOBILE ? 480 : 720;
const VIDEO_BPS = IS_MOBILE ? 300000 : 350000;
const AUDIO_BPS = 48000;
const CHUNK_MS = 5000;
const DIR = 'mesp-recordings';
const REC_NAME = /^rec-[a-f0-9]{24}-\d+\.webm$/;
// Files a ClassRecorder is writing right now (any mode). The uploader must
// never touch them — nor Chromium's `.crswap` staging files.
export const activeRecordingFiles = new Set();
export const recLockName = (name) => `mesp-rec-${name}`;
// Cross-tab guard (Web Locks): the recording tab holds an exclusive lock on
// its file for the whole class; uploaders in other tabs skip locked files.
export const withFileLock = async (name, fn) => {
  if (!navigator.locks?.request) return fn(true);
  return navigator.locks.request(recLockName(name), { ifAvailable: true }, (lock) => fn(!!lock));
};

const pickMime = () => {
  const c = ['video/webm;codecs=vp8,opus', 'video/webm;codecs=vp9,opus', 'video/webm'];
  for (const m of c) if (window.MediaRecorder?.isTypeSupported?.(m)) return m;
  return 'video/webm';
};

export const opfsDir = async () => {
  const root = await navigator.storage.getDirectory();
  return root.getDirectoryHandle(DIR, { create: true });
};

export const listPendingFiles = async () => {
  try {
    const dir = await opfsDir();
    const out = [];
    for await (const [name, handle] of dir.entries()) {
      if (handle.kind !== 'file' || !REC_NAME.test(name) || activeRecordingFiles.has(name)) continue;
      const f = await handle.getFile();
      if (f.size > 200000) out.push({ name, size: f.size });
      else if (Date.now() - f.lastModified > 10 * 60 * 1000) await dir.removeEntry(name).catch(() => {});
    }
    return out;
  } catch { return []; }
};

export const isRecorderSupported = () =>
  !!(window.MediaRecorder && navigator.storage?.getDirectory &&
     window.AudioContext && HTMLCanvasElement.prototype.captureStream);

export class ClassRecorder {
  constructor({ classroomId }) {
    this.classroomId = classroomId;
    this.active = false;
    this.startedAt = null;
    this._els = { canvas: null, video: null };
  }

  // Oct 2026 — start/stop are serialised. `stop()` called while `start()`
  // is still awaiting getUserMedia/OPFS used to return early (active was
  // still false) and leave an ORPHAN recorder encoding 720p for the rest
  // of the class (seen as two live MediaRecorders on a teacher laptop).
  async start() {
    if (this.active || this._starting || this._stopped || !isRecorderSupported()) return false;
    this._starting = this._start()
      .catch(async (e) => { await this._teardown(); throw e; })
      .finally(() => { this._starting = null; });
    return this._starting;
  }

  async _start() {
    const W = REC_W, H = REC_H;
    this.comp = document.createElement('canvas');
    this.comp.width = W; this.comp.height = H;
    const ctx = this.comp.getContext('2d');

    // audio: separate mic capture + every remote <audio> element's stream
    this.audioCtx = new AudioContext();
    this.dest = this.audioCtx.createMediaStreamDestination();
    try {
      this.micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      this.audioCtx.createMediaStreamSource(this.micStream).connect(this.dest);
    } catch { /* mic denied → record remote audio only */ }
    this._tapped = new WeakSet();
    this._tapAudioEls();

    // draw loop — the DOM scan (querySelectorAll + sort) is throttled to
    // once every 2s; frames in between reuse the cached element.
    this._visEl = null;
    this._scanTick = 0;
    const SCAN_EVERY = FPS * 2;
    this._drawTimer = setInterval(() => {
      try {
        if (this._scanTick % SCAN_EVERY === 0
            || !this._visEl || !this._visEl.isConnected) {
          this._visEl = this._pickVisual();
          this._tapAudioEls();
        }
        this._scanTick += 1;
        ctx.fillStyle = '#0B1020';
        ctx.fillRect(0, 0, W, H);
        const el = this._visEl;
        if (el) {
          const ew = el.videoWidth || el.width || 1280;
          const eh = el.videoHeight || el.height || 720;
          const s = Math.min(W / ew, H / eh);
          const dw = ew * s, dh = eh * s;
          ctx.drawImage(el, (W - dw) / 2, (H - dh) / 2, dw, dh);
        }
      } catch { /* keep loop alive */ }
    }, Math.round(1000 / FPS));

    const stream = this.comp.captureStream(FPS);
    this.dest.stream.getAudioTracks().forEach((t) => stream.addTrack(t));

    // OPFS incremental writer
    const dir = await opfsDir();
    this.fileName = `rec-${this.classroomId}-${Date.now()}.webm`;
    activeRecordingFiles.add(this.fileName);
    if (navigator.locks?.request) {
      await new Promise((acquired) => {
        navigator.locks.request(recLockName(this.fileName), () => new Promise((release) => {
          this._releaseLock = release; acquired();
        })).catch(() => acquired());
      });
    }
    const fh = await dir.getFileHandle(this.fileName, { create: true });
    this.writer = await fh.createWritable();
    this._writeQ = Promise.resolve();

    if (this._stopped) {
      // Unmounted while we were still setting up → release everything.
      await this._teardown();
      return false;
    }
    this.rec = new MediaRecorder(stream, {
      mimeType: pickMime(), videoBitsPerSecond: VIDEO_BPS, audioBitsPerSecond: AUDIO_BPS,
    });
    this.rec.ondataavailable = (e) => {
      if (e.data && e.data.size) {
        this._writeQ = this._writeQ.then(() => this.writer.write(e.data)).catch(() => {});
        try { this.onChunk?.(e.data); } catch { /* live uploader must never break recording */ }
      }
    };
    this.rec.start(CHUNK_MS);
    this.active = true;
    this.startedAt = Date.now();
    return true;
  }

  async _teardown() {
    clearInterval(this._drawTimer);
    this._drawTimer = null;
    try { this.micStream?.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
    try { this.dest?.stream?.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
    try { if (this.audioCtx && this.audioCtx.state !== 'closed') await this.audioCtx.close(); } catch { /* noop */ }
    try { await this.writer?.close(); } catch { /* noop */ }
    if (this.fileName) {
      try { await (await opfsDir()).removeEntry(this.fileName); } catch { /* noop */ }
      activeRecordingFiles.delete(this.fileName);
      this._releaseLock?.();
    }
  }

  _pickVisual() {
    // screen-share video wins; else the largest visible canvas (whiteboard)
    const vids = [...document.querySelectorAll('video')].filter(
      (v) => v.videoWidth > 300 && !v.paused && v.offsetParent !== null);
    if (vids.length) return vids.sort((a, b) => b.videoWidth - a.videoWidth)[0];
    const canvases = [...document.querySelectorAll('canvas')].filter(
      (c) => c !== this.comp && c.width > 200 && c.offsetParent !== null);
    if (canvases.length) {
      return canvases.sort((a, b) => (b.width * b.height) - (a.width * a.height))[0];
    }
    return null;
  }

  _tapAudioEls() {
    document.querySelectorAll('audio').forEach((el) => {
      const ms = el.srcObject;
      if (!ms || this._tapped.has(ms) || !ms.getAudioTracks?.().length) return;
      try {
        this.audioCtx.createMediaStreamSource(ms).connect(this.dest);
        this._tapped.add(ms);
      } catch { /* element without stream */ }
    });
  }

  async stop() {
    this._stopped = true;
    if (this._starting) {
      try { await this._starting; } catch { /* noop */ }
    }
    if (!this.active) {
      await this._teardown();
      return null;
    }
    this.active = false;
    clearInterval(this._drawTimer);
    const durationSec = Math.round((Date.now() - this.startedAt) / 1000);
    await new Promise((res) => {
      this.rec.onstop = res;
      try { this.rec.stop(); } catch { res(); }
    });
    await this._writeQ.catch(() => {});
    try { await this.writer.close(); } catch { /* already closed */ }
    activeRecordingFiles.delete(this.fileName);
    this._releaseLock?.();
    try { this.micStream?.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
    try { await this.audioCtx.close(); } catch { /* noop */ }
    // discard tiny/broken recordings (< 15s worth)
    try {
      const dir = await opfsDir();
      const f = await (await dir.getFileHandle(this.fileName)).getFile();
      if (f.size < 200000 || durationSec < 15) {
        await dir.removeEntry(this.fileName).catch(() => {});
        return null;
      }
    } catch { return null; }
    return { fileName: this.fileName, durationSec, classroomId: this.classroomId };
  }
}
